import { applyKeyframe } from '@noths/polaris-client-styles';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css, keyframes } from 'styled-components';

import { DELIVERY_MIN_HEIGHT, ELEVATION } from '../ProductCard/ProductCard.styles';
import { IMAGE_ASPECT_RATIO } from '../ProductCard/ProductCardMedia.styles';

const { borderRadius, colors, spacing, strokeWidth, typography } = themeVariables;

/* Every measurement below is the card's, imported or restated from
   ProductCard.styles.ts. The skeleton's job is to occupy exactly the space the
   card will, so anything that differs is a layout shift the customer sees the
   moment the products arrive. */

/* The placeholder bars are one colour at two opacities rather than a mix between
   two colours, so the pulse needs only the one token the card already uses for
   its own missing-image placeholder. */
const PULSE_MIN_OPACITY = 0.4;
const PULSE_DURATION = '1.6s';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: ${PULSE_MIN_OPACITY}; }
`;

/* Rounded to the same radius as the card's own small surfaces so the bars read
   as part of the same family rather than as raw rectangles. */
const BAR_RADIUS = '4px';

/* `applyKeyframe` rather than interpolating the keyframes directly: these styles
   reach the DOM through the `css` prop, which is only compiled by
   babel-plugin-styled-components. Under Jest that compilation does not happen,
   the block is stringified instead, and stringifying a Keyframes object throws.
   The shim yields the animation's name under test and the object everywhere
   else. */
const bar = css`
  display: block;
  border-radius: ${BAR_RADIUS};
  background-color: var(${colors.surface.disabled});
  animation: ${applyKeyframe(pulse)} ${PULSE_DURATION} ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const card = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: var(${borderRadius.md.mobile});
  background-color: var(${colors.surface.default});
  box-shadow: ${ELEVATION};
`;

export const image = css`
  ${bar}

  flex-shrink: 0;
  width: 100%;
  aspect-ratio: ${IMAGE_ASPECT_RATIO};
  border-radius: 0;
`;

export const body = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(${spacing.sm});
  min-height: 0;
  padding: var(${spacing.sm});
`;

export const details = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(${spacing.xxs});
`;

export const partner = css`
  ${bar}

  width: 60%;
  height: var(${typography.body.sizes.sm.lineHeight});
`;

/* The card clamps its title to two lines with no space between them, so the two
   bars are grouped and the details column's gap is kept off them. */
export const titleLines = css`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const titleLine = css`
  ${bar}

  height: var(${typography.heading.sizes.xs.lineHeight});
`;

/* A short second line, as a wrapped title almost always leaves. */
export const titleLineShort = css`
  ${titleLine}

  width: 70%;
`;

export const price = css`
  ${bar}

  width: 45%;
  height: var(${typography.body.sizes.md.lineHeight});
`;

export const divider = css`
  flex-shrink: 0;
  margin: 0;
  border: 0;
  border-top: var(${strokeWidth}) solid var(${colors.stroke.default});
`;

/* The same two lines and the same half-gap the delivery block uses, held to the
   same minimum height. */
export const deliverySlot = css`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  justify-content: center;
  gap: calc(var(${spacing.xxs}) / 2);
  min-height: ${DELIVERY_MIN_HEIGHT};
`;

const deliveryLine = css`
  ${bar}

  height: var(${typography.body.sizes.sm.lineHeight});
`;

export const deliveryLabel = css`
  ${deliveryLine}

  width: 80%;
`;

export const deliveryDate = css`
  ${deliveryLine}

  width: 50%;
`;
